import { Link } from "react-router-dom";
import type { Work } from "@/types";
import Badge from "@/components/base/Badge";
import { useFavorites } from "@/context/FavoritesContext";

interface WorkCardProps {
  work: Work;
  showFavorite?: boolean;
}

export default function WorkCard({ work, showFavorite = false }: WorkCardProps) {
  const { isFavorite, toggle } = useFavorites();
  const favored = isFavorite(work.id);

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
      <Link to={`/detail/work/${work.id}`} className="relative block aspect-[2/3] w-full overflow-hidden bg-background-200">
        <img
          src={work.poster}
          alt={`${work.title} 海报`}
          title={`${work.title} - ${work.genres.join("、")} - 亚洲天堂`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-primary-900/85 px-2 py-0.5 text-xs font-semibold text-background-50">
          {work.year}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-accent-500/95 px-2 py-0.5 text-xs font-bold text-background-50">
          <i className="ri-star-fill text-[10px]" aria-hidden="true"></i>
          {work.rating.toFixed(1)}
        </span>
      </Link>

      {showFavorite ? (
        <button
          type="button"
          onClick={() => toggle(work.id)}
          aria-label={favored ? "取消收藏" : "收藏"}
          className={`absolute right-2 top-11 flex h-7 w-7 items-center justify-center rounded-full text-sm transition-colors ${
            favored ? "bg-accent-500 text-background-50" : "bg-background-50/90 text-foreground-600 hover:text-accent-600"
          }`}
        >
          <i className={favored ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true"></i>
        </button>
      ) : null}

      <div className="flex flex-1 flex-col p-3">
        <h3 className="mb-1.5 truncate text-sm font-semibold text-foreground-900">
          <Link to={`/detail/work/${work.id}`} className="cursor-pointer transition-colors hover:text-primary-700">
            {work.title}
          </Link>
        </h3>
        <p className="mb-2 line-clamp-2 min-h-[2.4rem] text-xs leading-relaxed text-foreground-500">
          {work.synopsis}
        </p>
        <div className="mt-auto flex flex-wrap gap-1">
          {work.genres.slice(0, 2).map((g) => (
            <Badge key={g} tone="secondary" className="text-[10px]">
              {g}
            </Badge>
          ))}
          {work.type === "tv" && work.airStatus ? (
            <Badge tone={work.airStatus === "连载" ? "accent" : "outline"} className="text-[10px]">
              {work.airStatus}
            </Badge>
          ) : null}
        </div>
      </div>
    </article>
  );
}